import React, { useCallback, useState } from 'react';

import { useGetCards } from '@vlabs/api/artemy/client';
import type {
  Card, CardId, GetCardsParams,
} from '@vlabs/api/artemy/model';
import { Control } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import { BaseSamplePlaceholder } from '@vlabs/uikit/components/sample/base-sample/BaseSamplePlaceholder';
import type { WithDataTestid } from '@vlabs/uikit/types';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './AttachFaceToCard.module.sass';
import { CardSamplePoint3 } from '../../../shared/ui/card-sample';
import { GoToCardCircleAnimatedButton } from '../../go-to-card';
import { useAttachFaceToCardTranslation } from '../i18n';

interface AttachFaceToCardPoint3Props extends WithDataTestid {
  isPending?: boolean;
  onSubmit: (cardId: CardId) => void;
}

interface CardOption {
  value?: string;
  label: string;
  card: Card;
}

const displayName = 'AttachFaceToCardPoint3';
const testid = buildTestid(displayName);

function AttachFaceToCardPoint3({
  isPending = false,
  onSubmit,
  'data-testid': dataTestId = undefined,
}: AttachFaceToCardPoint3Props): React.JSX.Element {
  const { t } = useAttachFaceToCardTranslation();

  const [searchValue, setSearchValue] = useState('');
  const [selectedCard, setSelectedCard] = useState<Card>();

  const cardsByFio = useGetCards({
    'user_data.fio__ilike': `%${searchValue}%`,
    page_size: 5,
    targets: ['user_data'],
  } as GetCardsParams, {
    query: { enabled: !isPending },
  });

  const cardsById = useGetCards({
    card_id__ilike: `%${searchValue}%`,
    page_size: 5,
    targets: ['user_data'],
  }, {
    query: { enabled: !isPending },
  });

  const uniqueCards = new Map<CardId, Card>();
  [
    ...(cardsByFio.data?.cards ?? []),
    ...(cardsById.data?.cards ?? []),
  ].forEach((card) => {
    if (card.card_id) uniqueCards.set(card.card_id, card);
  });

  const cardOptions = Array.from(uniqueCards.values()).map((card) => ({
    value: card.card_id,
    label: card.user_data?.fio ?? card.card_id,
    card,
  }));

  const formatOptionLabel = useCallback((option: unknown) => {
    const { label, value } = option as CardOption;
    return (
      <div className={st.Option}>
        <span className={st.Fio}>{label}</span>
        <span className={st.Id}>{value}</span>
      </div>
    );
  }, []);

  return (
    <div className={st.Root}>
      <span className={st.Title}>
        {t('title')}
      </span>

      <div className={st.Body}>
        <Select
          className={st.SelectWrapper}
          disabled={isPending}
          formatOptionLabel={formatOptionLabel}
          inputValue={searchValue}
          isClearable
          isLoading={cardsByFio.isLoading || cardsById.isLoading}
          onChange={(cardOption) => setSelectedCard((cardOption as CardOption | null)?.card)}
          onInputChange={setSearchValue}
          options={cardOptions}
          placeholder={t('placeholder')}
        />

        {!selectedCard && (
          <div className={st.Placeholder}>
            <BaseSamplePlaceholder
              placeholderType="image"
            />
          </div>
        )}

        {!!selectedCard?.card_id && (
          <CardSamplePoint3
            buttonOnTop={(
              <GoToCardCircleAnimatedButton
                cardId={selectedCard.card_id}
                size="small"
              />
            )}
            cardId={selectedCard.card_id}
            className={st.Sample}
          />
        )}
      </div>

      <Control.Button
        className={st.ButtonWrapper}
        disabled={!selectedCard?.card_id || isPending}
        onClick={() => {
          if (selectedCard?.card_id) onSubmit(selectedCard.card_id);
        }}
        {...testid(dataTestId)}
      >
        {t('button_add')}
      </Control.Button>
    </div>
  );
}

AttachFaceToCardPoint3.displayName = displayName;

export { AttachFaceToCardPoint3 };
